import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import { DropdownItem, Badge } from 'reactstrap';
import Isvg from 'react-inlinesvg';
import classnames from 'classnames';

import quickFilterDates from 'app/utils/static/quickFilterDates';
import { unformatAmount, getTimeRange, parseAmount } from 'app/utils/parsers';

import PaymentBadge from 'app/components/general/PaymentBadge';

import { IMatchPayment } from 'app/ducks/matchPayments/types';

import history from 'app/routes/history';
import IconModal from 'app/components/general/IconModal';
import Table from 'app/components/general/TableNew';
import ModalConfirm from 'app/components/general/ModalConfirm';
import Button from 'app/components/general/Button';
import ActionDots from 'app/components/general/ActionDots';
import PlusIcon from 'app/images/icons/plus.svg';
import DeleteIcon from 'app/images/icons/delete.svg';
import EditIcon from 'app/images/icons/edit.svg';
import StatusBadge from 'app/components/general/StatusBadge';
import BadgeWithTooltip from 'app/components/general/BadgeWithTooltip';

import moment from 'moment';

import { getGroupSummary } from 'app/ducks/groups/selectors';
import { IGroup } from 'app/ducks/groups/types';
import CreateTeam from 'app/components/teams/CreateTeam';
import { parsePaymentStatus } from 'app/ducks/paymentEvents/selectors';
import ImportIcon from 'app/images/icons/upload.svg';

interface IProps {
  actions: any;
  competitions: any;
  matchPaymentsById: IGroup[];
  matchPaymentIds: string[];
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  selectTeam: (value) => void;
}

const MatchPaymentsTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  matchPaymentsById: undefined,
  matchPaymentIds: undefined,
  initialFilterConfig: {},
  persistentFilterConfig: {},
  isLoading: false,
  pagination: undefined,
  selectTeam: undefined,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [teamId, setTeamId] = useState(undefined);

  const filterOptions = [
    {
      label: 'Payment Status',
      accessor: 'subscriptionPaymentStatus',
      type: 'select',
      options: [
        { id: 'pending', name: 'Unpaid' },
        { id: 'paid', name: 'Teammo' },
        { id: 'paid-transferred', name: 'Transferred' },
        { id: 'paid-refunded', name: 'Refunded' },
        { id: 'walkover', name: 'Forfeit' },
        { id: 'paid-cash', name: 'Cash' },
        { id: 'paid-eftpos', name: 'Eftpos' },
        { id: 'paid-prepaid', name: 'Pre-paid' },
      ],
    },
    {
      label: 'Forfeit Status',
      accessor: 'subscriptionPaymentForfeitStatus',
      type: 'select',
      options: [
        { id: 'walkover', name: 'Forfeit' },
      ],
    },
    {
      label: 'Admin Status',
      accessor: 'subscriptionPaymentAdminStatus',
      type: 'select',
      options: [
        { id: 'no-show', name: 'No-show' },
        { id: 'opposition-no-show', name: 'Opposition No-show' },
        { id: 'cancelled', name: 'Cancelled' },
      ],
    },
    {
      label: 'Transfer Status',
      accessor: 'subscriptionPaymentTransferStatus',
      type: 'select',
      options: [
        { id: 'transfer-required', name: 'Transfer Pending' },
        { id: 'transferred', name: 'Transferred' },
        { id: 'refunded', name: 'Refunded' },
      ],
    },
    {
      label: 'Match Status',
      accessor: 'fixtureMatchStatus',
      type: 'select',
      options: [
        { id: 'active', name: 'Active' },
        { id: 'cancelled', name: 'Cancelled' },
      ],
    },
    {
      label: 'Match Date',
      accessor: 'roundLocalDateAt',
      type: 'datetime',
    },
    {
      label: 'Team',
      accessor: 'groupName',
      type: 'text',
    },
    // {
    //   label: 'Reference',
    //   accessor: 'paymentReference',
    //   type: 'text',
    // },
    {
      label: 'Round',
      accessor: 'description',
      type: 'text',
    },
    {
      label: 'Amount',
      accessor: 'amount',
      type: 'currency',
    },
    {
      label: 'Competition',
      accessor: 'competitionId',
      type: 'select',
      options: props.competitions?.activeCompetitionsByIdInSelectFormat,
    },
    {
      label: 'Section',
      accessor: 'groupSectionName',
      type: 'text',
    },
  ];

  const quickFilterOptions = [
    {
      label: 'Match Date',
      accessor: 'roundLocalDateAt',
      type: 'datetime',
    },
    {
      label: 'Team',
      accessor: 'groupName',
      type: 'text',
    },
    {
      label: 'Competition',
      accessor: 'competitionId',
      type: 'select',
      options: props.competitions?.activeCompetitionsByIdInSelectFormat,
    },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    if (
      props.appReady
      && !props.isLoading
      && (pageNumber !== props.pagination.page.number
        || (!_.isEmpty(sort) && sort !== props.pagination.sort)
        || filter !== props.pagination.filter)
    ) {
      // const groupName = _.find(sort, (f) => f.id === 'groupName');
      // if (groupName) {
      //   _.remove(sort, (f: any) => f.id === 'groupName');
      //   sort.push({ id: 'groups.name', desc: groupName.desc });
      // }
      // const groupSectionName = _.find(sort, (f) => f.id === 'groupSectionName');
      // if (groupSectionName) {
      //   _.remove(sort, (f: any) => f.id === 'groupSectionName');
      //   sort.push({ id: 'groups.sectionName', desc: groupSectionName.desc });
      // }
      // const groupSeasonName = _.find(sort, (f) => f.id === 'groupSeasonName');
      // if (groupSeasonName) {
      //   _.remove(sort, (f: any) => f.id === 'groupSeasonName');
      //   sort.push({ id: 'groups.seasonName', desc: groupSeasonName.desc });
      // }

      const amount = _.find(filter, (f) => f.id === 'amount');
      if (amount) {
        const cents = Math.trunc((unformatAmount(amount.value) * 100)).toString();
        _.remove(filter, (f: any) => f.id === 'amount');
        filter.push({ id: 'amount', value: cents });
      }
      const roundLocalDateAt = _.find(filter, (f) => f.id === 'roundLocalDateAt');
      if (roundLocalDateAt) {
        const { sinceTimeParsed, untilTimeParsed } = getTimeRange(roundLocalDateAt.value[0], roundLocalDateAt.value[1]);
        _.remove(filter, (f: any) => f.id === 'roundLocalDateAt');
        filter.push({ id: 'roundLocalDateAt.since', value: sinceTimeParsed });
        filter.push({ id: 'roundLocalDateAt.until', value: untilTimeParsed });
      }
      props.actions.getAllMatchPayments({
        page: {
          size: pageSize,
          number: pageNumber,
        },
        sort,
        filter,
      });
    }
  };

  const handleOpenModal = (e, selectedTeamId): void => {
    e.preventDefault();
    setDeleteModalOpen(true);
    setTeamId(selectedTeamId);
  };

  const handeCloseModal = (e): void => {
    e.preventDefault();
    setDeleteModalOpen(false);
    setTeamId(undefined);
  };

  const handleOpenEditModal = (e, selectedTeamId): void => {
    e.preventDefault();
    props.actions.selectTeam(selectedTeamId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = (e): void => {
    e.preventDefault();
    setEditModalOpen(false);
  };

  const handleOpenCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(true);
  };

  const handeCloseCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(false);
  };

  const handleDelete = (e): void => {
    e.preventDefault();
    props.actions.deleteTeam({ id: teamId });
  };

  const renderDeleteModal = (): React.ReactNode => (
    <ModalConfirm
      title="Delete Team Member"
      icon={DeleteIcon}
      description="Are you sure you want to delete this team member?"
      buttonText="Delete"
      isOpen={deleteModalOpen}
      isLoading={props.isLoading}
      handleSubmit={(e) => handleDelete(e)}
      handleCancel={handeCloseModal}
    />
  );

  const handleImportMatchPayments = (): void => {
    history.push(
      {
        pathname: '/import/match-payments',
        state: { modal: true, previousPath: props.match.path },
      },
    );
  };


  const handleClick = (id: string): void => {
    props.selectMatchPayment(id);
    history.push(`/payments/segments/matches/detail/${id}`);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.matchPaymentsById) || props.matchPaymentIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.matchPaymentIds,
      (obj, id) => {
        if (!_.isEmpty(props.matchPaymentsById[id])) {
          obj.push(props.matchPaymentsById[id]);
        }
        return obj;
      },
      [],
    );
    return rows;
  }, [props.matchPaymentsById, props.matchPaymentIds]);

  const smallColumn = 'col col-2 col-lg-2 col-xl-1';
  const flexColumn = 'col col-2 d-none d-md-flex col-xl-1';
  const mediumColumn = 'col col-6 col-md-3 col-lg-3 col-xl-2';
  const largeColumn = 'col col-6 col-md-4 col-lg-4 col-xl-3';

  const columns = useMemo(() => [
    {
      Header: 'Round',
      className: smallColumn,
      headerClassName: smallColumn,
      accessor: 'description',

      // accessor: (row) => ({ description: row.description, fixtureMatchStatus: row.fixtureMatchStatus }),
      // Cell: (cellProps) => (
      //   <div className="d-inline-flex align-items-center">
      //     <span className="d-inline-flex align-items-center">
      //       {cellProps.value.description}
      //     </span>
      //     <StatusBadge
      //       noActive
      //       defaultText=""
      //       className="margin-left-10"
      //       paymentText={cellProps.value.fixtureMatchStatus}
      //     />
      //   </div>

      // ),
    },
    {
      Header: 'Match Date',
      id: 'matchAt',
      accessor: (row) => ({ roundLocalDateAt: row.roundLocalDateAt, matchAt: row.matchAt }),
      // accessor: 'roundLocalDateAt',
      className: smallColumn,
      headerClassName: smallColumn,
      Cell: (cellProps) => moment(cellProps.value?.roundLocalDateAt || cellProps?.value?.matchAt).format('D MMM YYYY'),
    },
    // {
    //   id: 'status',
    //   Header: 'Payment',
    //   className: smallColumn,
    //   headerClassName: smallColumn,
    //   accessor: 'subscriptionPaymentStatus',
    //   Cell: (cellProps) => (
    //     <StatusBadge
    //       paymentText={cellProps.value}
    //     />
    //   ),
    // },
    {
      id: 'status',
      Header: 'Status',
      disableSortBy: true,
      className: largeColumn,
      headerClassName: largeColumn,
      accessor: (row) => {
        const statuses = parsePaymentStatus({
          paymentStatus: row.subscriptionPaymentStatus,
          paymentStatusDue: row.subscriptionPaymentDueStatus,
          transferStatus: row.subscriptionPaymentTransferStatus,
          adminStatus: row.subscriptionPaymentAdminStatus,
          forfeitStatus: row.subscriptionPaymentForfeitStatus,
          fixtureMatchStatus: row.fixtureMatchStatus,
        }, {
          paymentStatus: row.oppositionSubscriptionPaymentStatus,
          paymentStatusDue: row.subscriptionPaymentDueStatus,
          transferStatus: row.oppositionSubscriptionPaymentTransferStatus,
          adminStatus: row.oppositionSubscriptionPaymentAdminStatus,
          forfeitStatus: row.oppositionSubscriptionPaymentForfeitStatus,
        }, true);
        return {
          statuses,
          id: row.id,
        };
      },
      Cell: (cellProps: {
        value: {
          statuses: any[];
          id: string;
        };
      }) => {
        const targetId = `t-${cellProps.value.id}`;

        return (
          <div className="payment-badge-group">
            {
              _.map(cellProps.value.statuses, (statusBadge) => (
                <BadgeWithTooltip className="payment-badge" targetId={targetId} key={statusBadge.text} color={statusBadge.colour} tooltipText={statusBadge.description}>
                  {statusBadge.text}
                </BadgeWithTooltip>
              ))
            }
          </div>
        );
      },
    },
    // {
    //   id: 'transferStatus',
    //   Header: 'Transfer',
    //   className: smallColumn,
    //   headerClassName: smallColumn,
    //   accessor: 'subscriptionPaymentTransferStatus',
    //   Cell: (cellProps) => (
    //     <StatusBadge
    //       defaultText=""
    //       paymentText={cellProps.value}
    //     />
    //   ),
    // },
    // {
    //   id: 'fixtureMatchStatus',
    //   Header: 'Match',
    //   className: smallColumn,
    //   headerClassName: smallColumn,
    //   accessor: 'fixtureMatchStatus',
    //   Cell: (cellProps) => (
    //     <StatusBadge
    //       paymentText={cellProps.value}
    //     />
    //   ),
    // },
    {
      Header: 'Team',
      accessor: 'groupName',
      className: mediumColumn,
      headerClassName: mediumColumn,
    },

    {
      Header: 'Amount',
      accessor: 'amount',
      className: smallColumn,
      headerClassName: smallColumn,
      Cell: (cellProps) => parseAmount(cellProps.value),
    },
    {
      Header: 'Competition',
      accessor: 'competitionName',
      className: mediumColumn,
      headerClassName: mediumColumn,
    },
    {
      Header: 'Section',
      accessor: 'groupSectionName',
      className: mediumColumn,
      headerClassName: mediumColumn,
    },
  ], []);

  const counterText = props.pagination.meta.totalCount === 1 ? '1 match payment' : `${props.pagination.meta.totalCount || 0} match payments`;

  return (
    <div>
      {renderDeleteModal()}
      {/* <IconModal
        title="Create Match Payment"
        icon={PlusIcon}
        handleCancel={handeCloseCreateModal}
        isOpen={createModalOpen}
      >
        <CreateTeam {...props} teamId={props.teamId} afterSubmit={handeCloseCreateModal} />
      </IconModal> */}
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        // aggressiveLoader
        headerTitle={props.headerTitle}
        objectTitle="match payments"
        filterTitle="Match Payments Data"
        counterText={counterText}
        handleClick={handleClick}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        quickFilterOptions={quickFilterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
      >
        <Button onClick={handleImportMatchPayments} noLoading className="btn-thinner btn-2">
          <Isvg src={ImportIcon} className="btn-icon" />
          Import
        </Button>
        {/* <Button onClick={handleOpenCreateModal} noLoading className="btn-thinner btn-2">
          <Isvg src={PlusIcon} className="btn-icon" />
          New Match Payment
        </Button> */}
      </Table>
    </div>
  );
};

export default MatchPaymentsTable;
